import useResizeObserver from "@react-hook/resize-observer";
import { RefObject, useCallback, useEffect } from "react";

import { updateOverflowIndex, useTable } from "../context";

const toggleIconSize = 36;
const buttonGap = 4; // gap between buttons in ButtonContainer
const parentPadding = 4; // Toolbar container has padding, which we need to account for so buttons overflow before they reach the edge of the container
const effectiveButtonSize = toggleIconSize + buttonGap + parentPadding; // 44px per slot

// Toolbar styled component: padding: 8px, gap: 8px between its two grid columns.
const toolbarPaddingAndGap = 24; // 8 (left) + 8 (right) + 8 (gap)

// Minimum rendered width of DataTableColumnChooser. Derived from the
// StyledTreeSelect CSS: `.ant-select-selector { min-width: 400px }`.
// The column chooser occupies the `1fr` grid column and will never shrink
// below this value, so we use it as a floor when computing available button space.
const columnChooserMinWidth = 400;

/**
 * Pure formula: given measured toolbar and dropdown widths, returns how many
 * 36px action buttons fit in the available space.
 *
 * Each button slot is 44px (36px icon + 4px gap + 4px container padding).
 * The last button needs no trailing gap, so N buttons need N*44 - 4 px.
 * Solving for N: N = floor((available + 4) / 44).
 *
 * Exported for unit testing.
 */
export function computeOverflowIndex(
  toolbarWidth: number,
  dropdownWidth: number,
  reservedWidth = columnChooserMinWidth
): number {
  const availableWidth = Math.max(
    0,
    toolbarWidth - toolbarPaddingAndGap - reservedWidth - dropdownWidth
  );
  return Math.max(0, Math.floor((availableWidth + buttonGap) / effectiveButtonSize));
}

/**
 * Computes how many 36px action buttons fit in the toolbar's button area.
 *
 * Formula (left-to-right, no circular dependency):
 *   available = toolbar.width - chrome(24px) - columnChooserMin(250px) - dropdown.width
 *
 * `dropdown.width` is measured live so it automatically accounts for the
 * Groups/Wells toggle appearing or disappearing.
 *
 * Why not measure ButtonContainer directly?
 *   ButtonContainer lives in a `max-content` grid column and has flex-basis:0,
 *   so it collapses to min-width:36px at mount — creating a circular fixed-point
 *   that locks overflowIndex at 1 forever.
 *
 * Why not measure toolbarRight - dropdownRight?
 *   Same circular problem: at mount ButtonContainer is 36px, so dropdownRight
 *   is only 36px from toolbarRight regardless of how wide the toolbar is.
 */
const useOverflowIndex = (
  toolbarRef: RefObject<HTMLDivElement>,
  dropdownContainerRef: RefObject<HTMLDivElement>,
  // Width of anything else in the lead column (the monthly view switch and its gap).
  leadingWidth = 0
) => {
  const [, tableDispatch] = useTable();

  const recalc = useCallback(() => {
    const toolbar = toolbarRef.current;
    const dropdown = dropdownContainerRef.current;
    if (!toolbar || !dropdown) return;

    const index = computeOverflowIndex(
      toolbar.getBoundingClientRect().width,
      dropdown.getBoundingClientRect().width,
      columnChooserMinWidth + leadingWidth
    );
    updateOverflowIndex(tableDispatch, index);
  }, [dropdownContainerRef, leadingWidth, tableDispatch, toolbarRef]);

  // Re-run whenever the toolbar resizes (window / panel resize).
  useResizeObserver(toolbarRef, recalc);

  // Re-run whenever the dropdown container resizes (toggle appears/disappears,
  // pden-source select width changes, etc.).
  useResizeObserver(dropdownContainerRef, recalc);

  // Initial calculation and re-run on any dependency change.
  useEffect(() => {
    recalc();
  }, [recalc]);
};

export default useOverflowIndex;
